import type { BackupDTO, MigrationConsistency, MigrationKind, MigrationPlanDTO, MigrationRequest, ConfigConflictStrategy } from '@devrobot/shared';
import { MIGRATION_BACKUP_MAX_AGE_MINUTES } from '@devrobot/shared';

export const WIZARD_STEPS = [
  { id: 'source', label: 'Source' },
  { id: 'destination', label: 'Destination' },
  { id: 'scope', label: 'What to copy' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'safety', label: 'Back up first' },
  { id: 'review', label: 'Review' },
] as const;
export type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

export interface WizardState {
  step: number;
  project: string;
  sourceTargetId: string;
  /** Environment of the source target (to derive the migration kind). */
  sourceEnvironment: string;
  sourceVpsId: string;
  destEnvironment: string;
  destVpsId: string;
  include: MigrationRequest['include'];
  configConflict: ConfigConflictStrategy;
  overwriteVariables: string[];
  consistency: MigrationConsistency;
  cutover: boolean;
  overwriteDestination: boolean;
  acknowledgements: string[];
  /** `source|destEnv|destVps` whose first plan seeded the default selection. */
  seededFor: string | null;
  /** Required-backup key → chosen backup id. */
  backups: Record<string, string>;
}

export const initialWizardState: WizardState = {
  step: 0,
  project: '',
  sourceTargetId: '',
  sourceEnvironment: '',
  sourceVpsId: '',
  destEnvironment: '',
  destVpsId: '',
  include: { databases: [], volumes: [], config: { variables: [], files: [], vpsOverrides: true, targetOverrides: true } },
  configConflict: 'keep-destination',
  overwriteVariables: [],
  consistency: 'stop-source',
  cutover: false,
  overwriteDestination: false,
  acknowledgements: [],
  seededFor: null,
  backups: {},
};

type ListField = 'databases' | 'volumes' | 'variables' | 'files' | 'overwriteVariables' | 'acknowledgements';

export type WizardAction =
  | { type: 'go'; step: number }
  | { type: 'set-project'; project: string }
  | { type: 'set-source'; targetId: string; environment: string; vpsId: string; databases?: string[]; volumes?: string[] }
  | { type: 'set-destination'; environment?: string; vpsId?: string }
  | { type: 'seed'; plan: MigrationPlanDTO }
  | { type: 'toggle'; field: ListField; value: string; on: boolean }
  | { type: 'set-all'; field: ListField; values: string[] }
  | { type: 'set'; patch: Partial<Pick<WizardState, 'configConflict' | 'consistency' | 'cutover' | 'overwriteDestination'>> }
  | { type: 'set-overrides'; scope: 'vpsOverrides' | 'targetOverrides'; on: boolean }
  | { type: 'choose-backup'; key: string; backupId: string | null };

const toggled = (list: string[], value: string, on: boolean) => (on ? (list.includes(value) ? list : [...list, value]) : list.filter((v) => v !== value));

/** Everything that depends on the chosen source and destination starts over when either changes. */
const resetSelection = (s: WizardState): WizardState => ({
  ...s,
  include: initialWizardState.include,
  overwriteVariables: [],
  overwriteDestination: false,
  cutover: false,
  acknowledgements: [],
  seededFor: null,
  backups: {},
});

export function wizardReducer(s: WizardState, a: WizardAction): WizardState {
  switch (a.type) {
    case 'go':
      return { ...s, step: Math.max(0, Math.min(WIZARD_STEPS.length - 1, a.step)) };
    case 'set-project':
      if (a.project === s.project) return s;
      return resetSelection({ ...s, project: a.project, sourceTargetId: '', sourceEnvironment: '', sourceVpsId: '', destEnvironment: '', destVpsId: '' });
    case 'set-source': {
      if (a.targetId === s.sourceTargetId) return s;
      const next = resetSelection({ ...s, sourceTargetId: a.targetId, sourceEnvironment: a.environment, sourceVpsId: a.vpsId });
      return { ...next, include: { ...next.include, databases: a.databases ?? [], volumes: a.volumes ?? [] } };
    }
    case 'set-destination': {
      const environment = a.environment ?? s.destEnvironment;
      const vpsId = a.vpsId ?? s.destVpsId;
      if (environment === s.destEnvironment && vpsId === s.destVpsId) return s;
      const next = resetSelection({ ...s, destEnvironment: environment, destVpsId: vpsId });
      return { ...next, include: { ...next.include, databases: s.include.databases, volumes: s.include.volumes } };
    }
    case 'seed': {
      const key = selectionKey(s);
      if (s.seededFor === key) return s;
      const p = a.plan;
      return {
        ...s,
        seededFor: key,
        include: {
          databases: p.databases.map((d) => d.service),
          volumes: p.volumes.map((v) => v.name),
          config: { variables: p.config.variables.map((v) => v.name), files: p.config.files.map((f) => f.name), vpsOverrides: true, targetOverrides: true },
        },
        configConflict: 'keep-destination',
        overwriteVariables: [],
      };
    }
    case 'toggle':
    case 'set-all': {
      const apply = (list: string[]) => (a.type === 'toggle' ? toggled(list, a.value, a.on) : a.values);
      switch (a.field) {
        case 'databases':
        case 'volumes':
          return { ...s, include: { ...s.include, [a.field]: apply(s.include[a.field]) } };
        case 'variables': {
          const variables = apply(s.include.config.variables);
          return { ...s, include: { ...s.include, config: { ...s.include.config, variables } }, overwriteVariables: s.overwriteVariables.filter((v) => variables.includes(v)) };
        }
        case 'files':
          return { ...s, include: { ...s.include, config: { ...s.include.config, files: apply(s.include.config.files) } } };
        case 'overwriteVariables':
          return { ...s, overwriteVariables: apply(s.overwriteVariables) };
        case 'acknowledgements':
          return { ...s, acknowledgements: apply(s.acknowledgements) };
      }
      return s;
    }
    case 'set': {
      const next = { ...s, ...a.patch };
      if (a.patch.configConflict === 'keep-destination') next.overwriteVariables = [];
      return next;
    }
    case 'set-overrides':
      return { ...s, include: { ...s.include, config: { ...s.include.config, [a.scope]: a.on } } };
    case 'choose-backup': {
      const backups = { ...s.backups };
      if (a.backupId) backups[a.key] = a.backupId;
      else delete backups[a.key];
      return { ...s, backups };
    }
  }
}

export const selectionKey = (s: Pick<WizardState, 'sourceTargetId' | 'destEnvironment' | 'destVpsId'>) => `${s.sourceTargetId}|${s.destEnvironment}|${s.destVpsId}`;

export const hasDestination = (s: WizardState) => Boolean(s.sourceTargetId && s.destEnvironment && s.destVpsId && !isSameTarget(s));

export const isSameTarget = (s: Pick<WizardState, 'sourceEnvironment' | 'sourceVpsId' | 'destEnvironment' | 'destVpsId'>) =>
  Boolean(s.destEnvironment && s.destVpsId && s.destEnvironment === s.sourceEnvironment && s.destVpsId === s.sourceVpsId);

export function kindOf(s: Pick<WizardState, 'sourceEnvironment' | 'destEnvironment'>): MigrationKind | null {
  if (!s.sourceEnvironment || !s.destEnvironment) return null;
  return s.sourceEnvironment === s.destEnvironment ? 'server-move' : 'environment-copy';
}

export const KIND_LABEL: Record<MigrationKind, string> = {
  'server-move': 'Server move',
  'environment-copy': 'Environment copy',
};

export function toRequest(s: WizardState): MigrationRequest {
  const serverMove = kindOf(s) === 'server-move';
  return {
    project: s.project,
    sourceTargetId: s.sourceTargetId,
    destEnvironment: s.destEnvironment,
    destVpsId: s.destVpsId,
    include: s.include,
    configConflict: s.configConflict,
    ...(s.configConflict === 'overwrite-selected' ? { overwriteVariables: s.overwriteVariables } : {}),
    consistency: s.consistency,
    cutover: serverMove && s.cutover,
    overwriteDestination: s.overwriteDestination,
    acknowledgements: s.acknowledgements,
  };
}

export type RequiredBackup = MigrationPlanDTO['requiredBackups'][number];
export const backupKey = (b: RequiredBackup) => `${b.side}:${b.targetId}:${b.database}`;

/** A verified backup of that database, recent enough for the migration gate. */
export function isUsableBackup(b: BackupDTO | undefined, database: string, now = Date.now()): boolean {
  if (!b || b.state !== 'verified' || b.database.service !== database) return false;
  const at = Date.parse(b.createdAt);
  return Number.isFinite(at) && now - at <= MIGRATION_BACKUP_MAX_AGE_MINUTES * 60_000;
}

/** Selected data whose destination already holds data (overwrite must be acknowledged). */
export function destinationDataConflicts(plan: MigrationPlanDTO, s: WizardState): string[] {
  return [
    ...plan.databases.filter((d) => d.destinationHasData && s.include.databases.includes(d.service)).map((d) => `database ${d.service}`),
    ...plan.volumes.filter((v) => v.destinationHasData && s.include.volumes.includes(v.name)).map((v) => `volume ${v.name}`),
  ];
}

export function scopeIsEmpty(s: WizardState, plan: MigrationPlanDTO | undefined): boolean {
  const c = s.include.config;
  const overrides = plan?.config.overrides ?? [];
  const anyOverride = (c.vpsOverrides && overrides.some((o) => o.scope === 'vps')) || (c.targetOverrides && overrides.some((o) => o.scope === 'target'));
  return s.include.databases.length === 0 && s.include.volumes.length === 0 && c.variables.length === 0 && c.files.length === 0 && !anyOverride;
}

export interface PreviewState {
  /** The latest plan for the current source and destination (may lag behind the draft). */
  plan: MigrationPlanDTO | undefined;
  /** True when `plan` was computed for exactly the current draft. */
  upToDate: boolean;
  fetching: boolean;
  error: unknown;
  refetch: () => void;
}

export interface StepProps {
  state: WizardState;
  dispatch: (a: WizardAction) => void;
  preview: PreviewState;
}
